import {
    BilberryApiExceptionErrorDetails,
    BilberryApiValidationErroDetails,
    MembershipApiErrorDetails,
} from '@repo/types';

export class ApiError extends Error {
    // this.message behaves like a normal Error object and
    // contains the complete message with `title` and
    // `description` as a single string.

    // The reason we have separate `title` and `description`
    // fields, in addition to the normal `message` field,
    // is in cases where we want to render with separate
    // styling in the UI.
    public title: string;
    public description: string;
    public status: number;

    // Per-field validation errors from a 422 (base-api), keyed by field name.
    // `description` still holds the flattened, human-readable join for generic display.
    public fieldErrors?: Record<string, string[]>;

    constructor(
        title: string,
        description: string,
        status: number,
        fieldErrors?: Record<string, string[]>,
    ) {
        super(`${title}: \n${description}`);

        this.title = title;
        this.description = description;
        this.status = status;
        this.fieldErrors = fieldErrors;

        // Set the prototype explicitly.
        // Recommendation: https://github.com/Microsoft/TypeScript-wiki/blob/main/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, ApiError.prototype);
    }
}

type ParsedError = { messages: string[]; fieldErrors?: Record<string, string[]> };

async function parseResponse(response: Response, isMembership = false): Promise<ParsedError> {
    if (response.statusText === 'browser_timeout') return { messages: [await response.text()] };

    let jsonFailed = false;
    const data = await response.json().catch(() => {
        jsonFailed = true;
    });

    if (jsonFailed) return { messages: ['Unable to parse error'] };

    // If error originates from membership-api.
    if (isMembership) {
        const { detail, title } = data as MembershipApiErrorDetails;

        // Fallback to `title` if `detail` is empty.
        return { messages: [detail || title] };
    } else {
        // If error originates from base-api.

        // Validation error: keep the flattened messages AND the per-field map so callers
        // can show field-level feedback (e.g. register's duplicate-email → "log in" path).
        if (response.status === 422) {
            const { errors } = data as BilberryApiValidationErroDetails;
            return { messages: Object.values(errors).flat(), fieldErrors: errors };
        }

        // API Exception
        const { error, message } = data as BilberryApiExceptionErrorDetails;
        return { messages: [error?.message || message || ''] };
    }
}

export async function apiError(response: Response, isMembership = false): Promise<ApiError> {
    const parsed = await parseResponse(response, isMembership);

    return new ApiError(
        `API Error (${response.status})`,
        parsed.messages.join('\n'),
        response.status,
        parsed.fieldErrors,
    );
}
